'use client';

import Close from '@hc-frontend/core-icons/kit/close';
import { addClassName } from '@hc-frontend/core-utils-format';
import { createContext, useContext, useEffect, useMemo } from 'react';

import type { DialogContextProps, DialogProps } from './dialog.types';

export const DialogContext = createContext<DialogContextProps>({});

export function DialogCloseButton({
  onClick,
  className = 'absolute right-8-12',
  children = <Close />,
  ...props
}: React.ComponentProps<'button'>) {
  const { onClose } = useContext(DialogContext);
  return (
    <button
      onClick={(e) => {
        onClose?.();
        onClick?.(e);
      }}
      className={className}
      type="button"
      data-testid="close-dialog"
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Dialog component that renders a dialog with a backdrop. This dialog is designed to work with
 * Next.JS parallel routes and intercepting routes, therefore it relies on navigation to dismiss the modal.
 *
 * @public
 */
export function Dialog({
  children,
  className,
  onShow,
  onClose,
  dialogRef,
  ...props
}: DialogProps) {
  useEffect(() => {
    onShow?.();
  }, [onShow]);
  const value = useMemo<DialogContextProps>(
    () => ({
      onClose,
    }),
    [onClose],
  );

  return (
    <DialogContext.Provider value={value}>
      <div
        className="fixed left-0 top-0 z-[1000] m-0 h-[100vh] w-[100vw] bg-[rgba(0,0,0,0.65)] [max-inline-size:unset]"
        onClick={() => {
          onClose?.();
        }}
        aria-hidden="true"
        data-testid="backdrop"
      >
        <div
          className="hc-imposter-layout hc-container-layout"
          style={{
            '--hc-imposter-layout-margin': '0rem',
            '--hc-imposter-layout-position': 'fixed',
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          aria-hidden="true"
        >
          <div className="hc-container-layout">
            <div
              {...props}
              ref={dialogRef}
              onClick={(e) => {
                if (e.target === e.currentTarget) onClose?.();
              }}
              aria-hidden="true"
              data-testid="backdrop-content"
              className={addClassName(
                'text-center',
                '[&_>_*]:shadow-lg',
                '[&_>_*]:border-none',
                '[&_>_*]:w-10/12',
                '[&_>_*]:tablet:w-2/3',
                '[&_>_*]:max-h-[70vh]',
                'hc-center-layout',
                className,
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </DialogContext.Provider>
  );
}
